import React from "react";
import PropTypes from "prop-types";
import useFollowControl from "./use_follow_control";

/**
 * FollowControl — the shared follow/unfollow button (chrome + useFollowControl in one,
 * since no engine state depends on the follow lookup). Model-agnostic: the engine passes
 * the followable's Rails class name + id and decides where the button sits and for whom
 * it renders (typically not for a viewer who can edit the page — you don't follow
 * yourself). Renders nothing for anonymous viewers, before the followable id loads, or
 * unless the server exports PROFILE_FOLLOW_ENABLED (hidden by default).
 *
 * Follower/following counts are deliberately absent for now — the control ships before
 * the numbers are populated enough to show.
 *
 * @param {Object} props
 * @param {string} props.followableType - Rails class name (e.g. "Id::Profile::Promoter")
 * @param {number|string} [props.followableId] - Numeric id of the thing to follow
 * @param {string} [props.extraButtonClasses] - Spacing classes for the host layout
 */
function FollowControl({ followableType, followableId = null, extraButtonClasses = "mb-2 me-2" }) {
  const { enabled, isLoading, isFollowing, isMutating, toggle } = useFollowControl({
    followableType,
    followableId,
  });

  if (!enabled) return null;

  return (
    <button
      type="button"
      className={`btn text-nowrap d-block ${isFollowing ? "btn-primary" : "btn-outline-primary"} ${extraButtonClasses}`}
      onClick={toggle}
      disabled={isLoading || isMutating}
      aria-pressed={isFollowing}
    >
      <i className={`fa-solid ${isFollowing ? "fa-check" : "fa-plus"} me-2`} />
      {isFollowing ? "Following" : "Follow"}
    </button>
  );
}

FollowControl.propTypes = {
  followableType: PropTypes.string.isRequired,
  followableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extraButtonClasses: PropTypes.string,
};

export default FollowControl;
